/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { aiIntegrationsUpsertConfig } from "../funcs/aiIntegrationsUpsertConfig.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { AIIntegrationConfig } from "../models/components/aiintegrationconfig.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpsertAIIntegrationConfigRequest,
  UpsertAIIntegrationConfigSecurity,
} from "../models/operations/upsertaiintegrationconfig.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpsertAIIntegrationConfigMutationVariables = {
  request: UpsertAIIntegrationConfigRequest;
  security?: UpsertAIIntegrationConfigSecurity | undefined;
  options?: RequestOptions;
};

export type UpsertAIIntegrationConfigMutationData = AIIntegrationConfig;

export type UpsertAIIntegrationConfigMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * upsertConfig aiIntegrations
 *
 * @remarks
 * Create or update the org-wide AI integration config for a provider.
 */
export function useUpsertAIIntegrationConfigMutation(
  options?: MutationHookOptions<
    UpsertAIIntegrationConfigMutationData,
    UpsertAIIntegrationConfigMutationError,
    UpsertAIIntegrationConfigMutationVariables
  >,
): UseMutationResult<
  UpsertAIIntegrationConfigMutationData,
  UpsertAIIntegrationConfigMutationError,
  UpsertAIIntegrationConfigMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpsertAIIntegrationConfigMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpsertAIIntegrationConfig(): MutationKey {
  return ["@gram/client", "aiIntegrations", "upsertConfig"];
}

export function buildUpsertAIIntegrationConfigMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpsertAIIntegrationConfigMutationVariables,
  ) => Promise<UpsertAIIntegrationConfigMutationData>;
} {
  return {
    mutationKey: mutationKeyUpsertAIIntegrationConfig(),
    mutationFn: function upsertAIIntegrationConfigMutationFn({
      request,
      security,
      options,
    }): Promise<UpsertAIIntegrationConfigMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(aiIntegrationsUpsertConfig(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
